import { api } from '@/lib/api'

/** What POST issue-photo returns and a flagged issue stores. */
export interface IssuePhoto {
  photo_path: string
  photo_key: string
}

export const ISSUE_PHOTO_ACCEPT = 'image/*'

/** Long edge after downscaling. Enough to read a misprint, small enough to send on 4G. */
const MAX_EDGE = 1600
const QUALITY = 0.82

/**
 * Shrink a phone photo before it leaves the device.
 *
 * Originals run 3 to 8 MB, uploads sit on a small mounted volume in
 * production, and the floor is on mobile data. Anything the browser cannot
 * decode (HEIC on some devices) goes up untouched, which the API accepts.
 */
async function downscale(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))

    // Already small in both senses: sending the original avoids a needless
    // re-encode that would only lose detail.
    if (scale === 1 && file.size <= 1_000_000) {
      bitmap.close()
      return file
    }

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const context = canvas.getContext('2d')
    if (!context) {
      bitmap.close()
      return file
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY))
    if (!blob) return file

    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
  } catch {
    return file
  }
}

/**
 * Upload the picture that goes with a flagged issue. The endpoint differs by
 * portal (`/staff/issue-photo`, `/partner/issue-photo`) because the role gate
 * does, so the caller passes its own.
 */
export async function uploadIssuePhoto(file: File, endpoint: string): Promise<IssuePhoto> {
  const form = new FormData()
  form.append('photo', await downscale(file))

  return (await api.post<IssuePhoto>(endpoint, form)).data
}
